import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';

const LegalLayout = ({ title, kicker, lastUpdated, children, path, description }) => (
  <div className="min-h-screen bg-stone-50">
    <SEO
      title={title}
      description={description}
      path={path}
      schema={[
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: title,
          url: `https://akirasignaturecare.com${path}`,
          inLanguage: 'en-US',
          isPartOf: { '@id': 'https://akirasignaturecare.com/#website' },
        },
      ]}
    />
    {/* Hero */}
    <section className="relative bg-gradient-to-br from-stone-900 via-stone-800 to-rose-900 text-white py-24">
      <div className="container mx-auto px-6 max-w-4xl">
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-rose-400" />
          <span className="text-rose-300 text-xs tracking-[0.4em] uppercase font-light">
            {kicker}
          </span>
        </div>
        <h1
          className="font-serif text-4xl lg:text-5xl mb-4 leading-tight"
          style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
        >
          {title}
        </h1>
        <p className="text-stone-300 text-sm font-light">
          Last updated: {lastUpdated}
        </p>
      </div>
    </section>

    {/* Content */}
    <section className="py-20">
      <div className="container mx-auto px-6 max-w-3xl">
        <article
          className="prose prose-stone max-w-none prose-headings:font-serif prose-headings:text-stone-900 prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-p:text-stone-700 prose-p:leading-relaxed prose-a:text-rose-500 hover:prose-a:text-rose-600 prose-strong:text-stone-900 prose-li:text-stone-700"
          style={{ fontFamily: 'inherit' }}
        >
          {children}
        </article>
      </div>
    </section>
  </div>
);

export const PrivacyPolicyPage = () => (
  <LegalLayout
    title="Privacy Policy"
    kicker="Legal"
    lastUpdated="June 11, 2026"
    path="/privacy"
    description="Akira Signature Care's privacy policy — how we collect, use, and protect your personal and health information, including SMS communications."
  >
    <p>
      Akira Signature Care (&ldquo;<strong>Akira</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo;
      &ldquo;<strong>our</strong>,&rdquo; or &ldquo;<strong>us</strong>&rdquo;) is committed to
      protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and
      safeguard your information when you visit our website,{' '}
      <a href="https://akirasignaturecare.com">akirasignaturecare.com</a> (the
      &ldquo;<strong>Site</strong>&rdquo;), book a consultation, become a patient, or otherwise
      interact with us.
    </p>

    <h2>1. Information We Collect</h2>
    <h3>Information you provide directly</h3>
    <ul>
      <li>
        <strong>Contact and identification details:</strong> name, email address, mobile phone
        number, mailing address, date of birth.
      </li>
      <li>
        <strong>Health and treatment information:</strong> medical history, current medications,
        treatment goals, photographs, lab results, and any other clinical information you share
        during a consultation or treatment.
      </li>
      <li>
        <strong>Payment information:</strong> payment card or bank details processed by our
        payment processors. We do not store full payment card numbers on our servers.
      </li>
      <li>
        <strong>Communications:</strong> messages you send us via our contact form, email, SMS,
        chat, or social media.
      </li>
    </ul>
    <h3>Information collected automatically</h3>
    <ul>
      <li>
        Device, browser, IP address, referring URL, and pages viewed (used for analytics and to
        secure the Site).
      </li>
      <li>Cookies and similar technologies (see &ldquo;Cookies&rdquo; below).</li>
    </ul>

    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>To schedule, deliver, and follow up on consultations and treatments.</li>
      <li>
        To respond to enquiries, send appointment confirmations and reminders, billing
        notifications, and important account updates.
      </li>
      <li>To send marketing communications where you have opted in to receive them.</li>
      <li>To improve our Site, services, and patient experience.</li>
      <li>
        To comply with legal, regulatory, and professional obligations, including those under
        HIPAA where applicable.
      </li>
    </ul>

    <h2>3. SMS / Text-Message Communications</h2>
    <p>
      <strong>
        By opting into SMS from a web form or other medium, you are agreeing to receive SMS
        messages from Akira Signature Care. This includes SMS messages for customer care,
        account notifications, and marketing. Message frequency varies. Message and data rates
        may apply.
      </strong>
    </p>
    <p>
      You can opt out at any time by replying <strong>STOP</strong> to any message you receive
      from us. For help, reply <strong>HELP</strong> or contact us at{' '}
      <a href="tel:+16267373183">(626) 737-3183</a>. We do not share your mobile number or SMS
      consent with third parties or affiliates for their own marketing purposes. Carriers are not
      liable for delayed or undelivered messages.
    </p>

    <h2>4. Protected Health Information (PHI) &amp; HIPAA</h2>
    <p>
      When Akira Signature Care collects information that constitutes Protected Health
      Information (PHI) under the Health Insurance Portability and Accountability Act
      (&ldquo;HIPAA&rdquo;), we maintain that information in accordance with applicable federal
      and California state law. Our separate HIPAA Notice of Privacy Practices is provided to
      patients at the start of care and on request.
    </p>

    <h2>5. How We Share Your Information</h2>
    <p>We do not sell your personal information. We may share information only:</p>
    <ul>
      <li>
        With <strong>service providers</strong> who perform services on our behalf (e.g. EHR,
        scheduling, payment processing, email and SMS delivery, analytics) and who are bound by
        confidentiality obligations.
      </li>
      <li>
        With <strong>healthcare providers</strong> involved in your care, with your authorisation.
      </li>
      <li>
        Where <strong>required by law</strong>, court order, or to protect the rights, property,
        or safety of Akira, our patients, or others.
      </li>
      <li>
        In connection with a <strong>business transaction</strong> such as a merger, acquisition,
        or sale of assets, subject to confidentiality protections.
      </li>
    </ul>

    <h2>6. Cookies and Analytics</h2>
    <p>
      We use cookies and similar technologies to remember preferences, analyse traffic, and
      improve the Site. You can disable cookies in your browser settings; some features of the
      Site may not function properly without them.
    </p>

    <h2>7. Data Retention</h2>
    <p>
      We retain personal information for as long as necessary to provide our services, comply
      with our legal obligations, resolve disputes, and enforce our agreements. Medical records
      are retained in accordance with California and federal record-retention requirements.
    </p>

    <h2>8. Your Choices and California Privacy Rights</h2>
    <p>You may:</p>
    <ul>
      <li>Request access to or correction of your personal information on file with us.</li>
      <li>Withdraw consent for marketing communications at any time.</li>
      <li>Opt out of SMS by replying <strong>STOP</strong>.</li>
      <li>
        Exercise rights under the California Consumer Privacy Act (CCPA) / California Privacy
        Rights Act (CPRA), including the right to know, right to delete, right to correct, and
        right to limit use of sensitive personal information, subject to applicable healthcare
        exemptions.
      </li>
    </ul>
    <p>
      To exercise any of these rights, email{' '}
      <a href="mailto:info@akirasignaturecare.com">info@akirasignaturecare.com</a> or call{' '}
      <a href="tel:+16267373183">(626) 737-3183</a>.
    </p>

    <h2>9. Security</h2>
    <p>
      We implement administrative, technical, and physical safeguards designed to protect your
      information. No method of transmission over the internet or electronic storage is 100%
      secure, and we cannot guarantee absolute security.
    </p>

    <h2>10. Children&rsquo;s Privacy</h2>
    <p>
      Our services are not directed to individuals under 18. We do not knowingly collect
      personal information from children. If you believe a child has provided us information,
      please contact us so we can delete it.
    </p>

    <h2>11. Changes to this Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. The &ldquo;Last updated&rdquo; date
      above reflects the most recent revision. Material changes will be communicated through the
      Site.
    </p>

    <h2>12. Contact Us</h2>
    <p>
      Akira Signature Care
      <br />
      506 North Garfield Avenue, Ste 210, Alhambra, CA 91801
      <br />
      Phone: <a href="tel:+16267373183">(626) 737-3183</a>
      <br />
      Email: <a href="mailto:info@akirasignaturecare.com">info@akirasignaturecare.com</a>
    </p>
  </LegalLayout>
);

export const TermsOfServicePage = () => (
  <LegalLayout
    title="Terms of Service"
    kicker="Legal"
    lastUpdated="June 11, 2026"
    path="/terms"
    description="Terms of Service for Akira Signature Care — the agreement between you and Akira for using our website and booking services."
  >
    <p>
      These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) govern your use of the
      website operated by Akira Signature Care (&ldquo;<strong>Akira</strong>,&rdquo;
      &ldquo;<strong>we</strong>,&rdquo; &ldquo;<strong>our</strong>,&rdquo; or
      &ldquo;<strong>us</strong>&rdquo;) at{' '}
      <a href="https://akirasignaturecare.com">akirasignaturecare.com</a> (the
      &ldquo;<strong>Site</strong>&rdquo;). By accessing or using the Site, you agree to be
      bound by these Terms. If you do not agree, do not use the Site.
    </p>

    <h2>1. Not Medical Advice</h2>
    <p>
      The Site, including all articles, treatment descriptions, FAQs, and other content, is
      provided for general information only and <strong>is not medical advice</strong>. Reading
      this content does not create a physician&ndash;patient relationship. Always seek the
      advice of a qualified healthcare professional regarding any medical condition or
      treatment.
    </p>

    <h2>2. Booking, Consultations, and Treatments</h2>
    <p>
      A booking made through the Site is a request for an appointment. All consultations and
      treatments are subject to a clinical assessment by an Akira physician or licensed
      provider, who may decline to provide a service if it is not clinically appropriate.
      Outcomes vary; we do not guarantee specific results.
    </p>

    <h2>3. Eligibility</h2>
    <p>
      You must be at least 18 years old to book a treatment, consent to care, or create an
      account. By using the Site, you represent that you meet this requirement.
    </p>

    <h2>4. Fees, Payment, and Cancellations</h2>
    <p>
      Treatment fees are quoted at consultation and may be updated periodically. We reserve the
      right to require a deposit to hold an appointment. Please review our current cancellation
      and no-show policy provided at the time of booking. Refunds, where applicable, are issued
      in accordance with that policy.
    </p>

    <h2>5. Acceptable Use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>Use the Site in any unlawful, harmful, or fraudulent manner.</li>
      <li>
        Attempt to gain unauthorised access to any part of the Site, our servers, or any
        connected systems.
      </li>
      <li>
        Reverse engineer, scrape, or otherwise extract data from the Site except as permitted by
        law.
      </li>
      <li>
        Submit content that is false, defamatory, infringing, obscene, or otherwise objectionable.
      </li>
    </ul>

    <h2>6. Intellectual Property</h2>
    <p>
      All content on the Site &mdash; including text, articles, photographs, graphics, logos,
      and the Akira Signature Care name and marks &mdash; is owned by or licensed to Akira and
      is protected by copyright, trademark, and other applicable laws. You may view and share
      links to articles for personal, non-commercial use only. Any other use requires our prior
      written permission.
    </p>

    <h2>7. Third-Party Links and Services</h2>
    <p>
      The Site may contain links to third-party websites or use third-party services (e.g.
      booking, payment, analytics). We are not responsible for the content or practices of those
      third parties. Your interactions with them are governed by their own terms and privacy
      policies.
    </p>

    <h2>8. SMS and Email Communications</h2>
    <p>
      By providing your mobile number or email address, you authorise Akira to contact you about
      your appointments and care. Marketing communications are sent only with your opt-in
      consent. For details on SMS, see our{' '}
      <Link to="/privacy">Privacy Policy</Link>.
    </p>

    <h2>9. Disclaimers</h2>
    <p>
      The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To
      the fullest extent permitted by law, Akira disclaims all warranties, express or implied,
      including merchantability, fitness for a particular purpose, and non-infringement. We do
      not warrant that the Site will be uninterrupted, error-free, secure, or free from harmful
      components.
    </p>

    <h2>10. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by law, Akira and its officers, employees, contractors,
      and agents shall not be liable for any indirect, incidental, special, consequential, or
      punitive damages arising from or relating to your use of the Site, even if advised of the
      possibility of such damages. Nothing in these Terms limits any liability that cannot
      lawfully be limited, including liability for personal injury caused by negligence.
    </p>

    <h2>11. Indemnification</h2>
    <p>
      You agree to indemnify and hold harmless Akira from any claims, damages, liabilities,
      costs, and expenses (including reasonable legal fees) arising from your breach of these
      Terms or your misuse of the Site.
    </p>

    <h2>12. Governing Law and Venue</h2>
    <p>
      These Terms are governed by the laws of the State of California, without regard to its
      conflict-of-laws principles. Any dispute arising out of or related to these Terms or the
      Site shall be brought exclusively in the state or federal courts located in Los Angeles
      County, California, and you consent to the personal jurisdiction of those courts.
    </p>

    <h2>13. Changes to these Terms</h2>
    <p>
      We may update these Terms from time to time. The &ldquo;Last updated&rdquo; date at the
      top reflects the most recent version. Continued use of the Site after a change
      constitutes acceptance of the updated Terms.
    </p>

    <h2>14. Contact</h2>
    <p>
      Questions about these Terms can be sent to{' '}
      <a href="mailto:info@akirasignaturecare.com">info@akirasignaturecare.com</a> or by mail to
      Akira Signature Care, 506 North Garfield Avenue, Ste 210, Alhambra, CA 91801.
    </p>
  </LegalLayout>
);
